'use client';
import React, { useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';
import type { StockPosition } from './StockPositionsContent';

export default function StockTable({ items }: { items: StockPosition[] }) {
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  if (items.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-12 text-center shadow-card">
        <Icon name="ChartBarIcon" size={24} className="text-muted-foreground mx-auto mb-3" />
        <p className="text-sm font-600 text-foreground">No stock records match your filters</p>
        <p className="text-xs text-muted-foreground mt-1">Adjust the site, health, or search filters above.</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl shadow-card overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              {['Item Code', 'Item Name', 'Site', 'On-Hand', 'Reserved', 'Available', 'Reorder Lvl', 'Stock Health', 'Actions'].map((h) => (
                <th key={`stk-th-${h}`} className="text-left text-[10px] font-600 uppercase tracking-wider text-muted-foreground px-3 py-3 whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((item, idx) => {
              const isLow = item.health === 'LOW' || item.health === 'CRITICAL';
              const isOut = item.health === 'OUT_OF_STOCK';
              return (
                <tr
                  key={item.id}
                  onMouseEnter={() => setHoveredRow(item.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  className={`transition-colors ${idx % 2 === 1 ? 'bg-muted/20' : ''} ${
                    isOut ? 'bg-red-50/50' : isLow ? 'bg-amber-50/30' : ''
                  } ${hoveredRow === item.id ? 'bg-secondary/5' : ''}`}
                >
                  <td className="px-3 py-3">
                    <span className="font-mono-data text-xs text-foreground font-500">{item.itemCode}</span>
                  </td>
                  <td className="px-3 py-3 max-w-[160px]">
                    <span className="text-sm text-foreground truncate block">{item.itemName}</span>
                    <span className="text-[11px] text-muted-foreground">{item.category.replace('_', ' ')}</span>
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">{item.site}</td>
                  <td className="px-3 py-3">
                    <span className="text-sm font-600 tabular-nums text-foreground">{item.onHand.toLocaleString('de-DE')}</span>
                    <span className="text-xs text-muted-foreground ml-1">{item.uom}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs tabular-nums text-amber-600 font-500">{item.reserved.toLocaleString('de-DE')} {item.uom}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-sm font-700 tabular-nums ${isOut ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-teal'}`}>
                      {item.available.toLocaleString('de-DE')} {item.uom}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs tabular-nums text-muted-foreground">{item.reorderLevel.toLocaleString('de-DE')} {item.uom}</span>
                    </div>
                    <div className="w-16 h-1 bg-muted rounded-full mt-1 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${isOut ? 'bg-red-500' : isLow ? 'bg-amber-400' : 'bg-teal'}`}
                        style={{ width: `${Math.min(Math.round((item.available / item.reorderLevel) * 100), 100)}%` }}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <StatusBadge
                      status={item.health === 'OUT_OF_STOCK' ? 'OUT_OF_STOCK' : item.health === 'CRITICAL' ? 'CRITICAL' : item.health === 'LOW' ? 'LOW' : 'HEALTHY'}
                      size="sm"
                    />
                  </td>
                  <td className="px-3 py-3">
                    <div className={`flex items-center gap-1 transition-opacity ${hoveredRow === item.id ? 'opacity-100' : 'opacity-0'}`}>
                      <button
                        title="View item detail"
                        onClick={() => toast.info(`Item detail: ${item.itemCode}`)}
                        className="p-1.5 rounded-md hover:bg-secondary/10 text-secondary transition-colors"
                      >
                        <Icon name="EyeIcon" size={14} />
                      </button>
                      <button
                        title="Raise transfer order"
                        onClick={() => toast.info(`Transfer order for ${item.itemCode}`)}
                        className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Icon name="ArrowsRightLeftIcon" size={14} />
                      </button>
                      {(isLow || isOut) && (
                        <button
                          title="Raise purchase order"
                          onClick={() => toast.success(`PO raised for ${item.itemCode}`)}
                          className="p-1.5 rounded-md hover:bg-amber-50 text-amber-600 transition-colors"
                        >
                          <Icon name="ShoppingCartIcon" size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 border-t border-border">
        <span className="text-xs text-muted-foreground">
          {items.length} stock position{items.length !== 1 ? 's' : ''} shown
        </span>
      </div>
    </div>
  );
}